import { PrinterOutlined } from "@ant-design/icons";
import { ArrowBack } from "@mui/icons-material";
import { Breadcrumbs, Card, CardHeader, Divider, Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { InputNumber, Pagination, Result, Space, Spin, Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import MDBox from "~/components/MDBox";
import MDButton from "~/components/MDButton";
import MDTypography from "~/components/MDTypography";
import DashboardLayout from "~/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "~/examples/Navbars/DashboardNavbar";
import usePagination from "~/hooks/usePagination";
import dashboardServices from "~/services/dashboard.services";
import BarCodeComponent from "./BarCodeComponent";

const QrCodeGeneration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const componentRef = useRef();
  const initialPageSize = 100;

  const [inputValue, setInputValue] = useState(25);

  const onChange = (newValue) => {
    setInputValue(newValue);
  };

  const handlePrint = useReactToPrint({
    pageStyle: `@media print {


      .print-element {
        page-break-inside: avoid; /* Avoid breaking elements across pages */
      }
    }`,
    content: () => componentRef.current,
  });

  const { data, isLoading, mutateAsync, mutate, reset } = useMutation({
    mutationKey: ["qrCode", location.state?.Plantation_Auto_ID],
    mutationFn: (payloads) =>
      dashboardServices.getMonitoringPlantList(payloads),
  });

  const { currentPage, handlePageChange, pageSize } = usePagination(
    mutate,
    location.state?.Plantation_Auto_ID,
    initialPageSize
  );

  useEffect(() => {
    mutate({ id: location.state?.Plantation_Auto_ID, currentPage, pageSize });

    return () => {
      reset();
    };
  }, []);

  const dynamicHeader = [
    {
      title: "Location",
      value: location.state["plot_mstr.loc_mstr.Loc_Name"],
    },
    {
      title: "Plot Code",
      value: location.state["plot_mstr.Plot_Code"],
    },
    {
      title: "Plotting Name",
      value: location.state["plot_mstr.Plot_Name"],
    },
  ];

  const renderDynamicHeader = () => {
    return (
      <>
        <Breadcrumbs
          sx={{
            "& .MuiBreadcrumbs-separator": {
              color: ({ palette: { white, grey } }) => grey[600],
            },
          }}
        >
          {dynamicHeader.map((item) => (
            <MDBox
              key={item.title}
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <MDTypography
                component="span"
                variant="button"
                fontWeight="regular"
                textTransform="capitalize"
                color={"dark"}
                sx={{ lineHeight: 0 }}
                gutterBottom
              >
                {item.title}
              </MDTypography>

              <Divider sx={{ marginTop: 0.4, marginBottom: 0.4 }} />
              <MDTypography
                variant="caption"
                textTransform="capitalize"
                opacity={0.7}
              >
                {item.value}
              </MDTypography>
            </MDBox>
          ))}
        </Breadcrumbs>
      </>
    );
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card>
        <MDBox>
          <CardHeader
            action={
              <Space>
                <InputNumber
                  addonAfter={
                    <Tooltip title="Print">
                      <PrinterOutlined onClick={handlePrint} />
                    </Tooltip>
                  }
                  size="large"
                  min={1}
                  max={100}
                  style={{ margin: "0 16px", width: "8rem" }}
                  value={inputValue}
                  onChange={onChange}
                />
                {/* <MDButton
                  variant="contained"
                  color="primary"
                  aria-label="navigation"
                  startIcon={<PrintOutlined />}
                  onClick={handlePrint}
                >
                  Print
                </MDButton> */}
                <MDButton
                  variant="contained"
                  color="secondary"
                  aria-label="navigation"
                  startIcon={<ArrowBack />}
                  onClick={() => navigate(-1, { replace: true })}
                >
                  Back
                </MDButton>
              </Space>
            }
            title={
              <MDBox pt={1} mb={0.5}>
                <MDTypography fontWeight="bold" color="text" gutterBottom>
                  {`QR Code of `}
                  {
                    <MDTypography
                      variant="span"
                      fontWeight="bold"
                      color="success"
                    >
                      {`${location.state["plant_mstr.Plant_Local_Name"]}`}
                    </MDTypography>
                  }
                </MDTypography>
              </MDBox>
            }
            subheader={renderDynamicHeader()}
            // subheader={`Plot details :> ${location.state['plot_mstr.loc_mstr.Loc_Name']}/${location.state['plot_mstr.Plot_Code']}/${location.state['plot_mstr.Plot_Name']}`}
          />
          {/* <MDTypography variant="caption">
            Number of Plants : {location.state?.total_plant}
          </MDTypography> */}
        </MDBox>

        <MDBox mt={2}>
          <MDBox mb={3} p={2}>
            <Spin tip="loading..." spinning={isLoading}>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={12} md={12}>
                  {/* Component ref is responsible for print area you want */}
                  <Grid
                    sx={{
                      display: "grid",
                      padding: "1rem 1rem",
                      gridTemplateColumns:
                        "repeat(4, 1fr)" /* 2 columns per row */,
                      // gridTemplateRows: "repeat(3, 1fr)",
                      // gridColumnGap: "2rem",
                      // gridRowGap: "3rem",
                      justifyItems: "center",
                      gridGap: "1rem" /* Adjust the gap as needed */,
                    }}
                    ref={componentRef}
                  >
                    {data?.data.slice(0, inputValue).map((item) => (
                      <MDBox key={item?.BarCode_Number}>
                        <BarCodeComponent item={item} />
                      </MDBox>
                    ))}
                  </Grid>
                </Grid>
                {data?.data?.length === 0 ? (
                  <Grid item xs={12} md={12} xl={12} lg={12}>
                    <Result
                      status="404"
                      title="No Content"
                      // subTitle="No Data to show"
                      //   extra={<Button type="primary">Back Home</Button>}
                    />
                  </Grid>
                ) : null}
                <Grid item lg={12} justifyContent="center">
                  <Pagination
                    showSizeChanger
                    defaultPageSize={100}
                    defaultCurrent={currentPage}
                    total={data?.count ?? 10}
                    onChange={handlePageChange}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`
                    }
                  />
                </Grid>
              </Grid>
            </Spin>
          </MDBox>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
};

export default QrCodeGeneration;
